import { PDFDocument, StandardFonts } from '@cantoo/pdf-lib';
import { downloadFile, normalizeStringRelaxedASCII, readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { format, parseISO, startOfMonth, subDays } from 'date-fns';
import { client } from '../../config/api';
import { GetOfficeDocument, TDependantType, TGiftTypes } from '../../generated/graphql';
import { getReadableWorkAnniversaryVariation } from '../companies/shared';
import { isDependant } from '../recipients/shared';
import { TGiftType } from './queries';
import { getGiftEventType } from './utils';

export type TGetGiftInfoSupportProps = {
  type: TGiftTypes;
};

export const getRemovedInfoFromRecipientData = (gift: any) => {
  const recipientData = gift.recipientData ?? {};
  if (recipientData.fullName) return `${recipientData.fullName} (Removed)`;
  if (!recipientData.firstName && !recipientData.lastName) return '-';
  return `${recipientData.firstName ?? '[No first name found]'} ${recipientData.lastName ?? '[No last name found]'} (Removed)`;
};

type TCalculatedShippingDateProps = {
  date: string | Date;
  address: { country: string; state: string };
};

export const calculatedShippingDate = (props: TCalculatedShippingDateProps) => {
  const { date, address } = props;
  const refDate = typeof date === 'string' ? parseISO(date) : date;

  let deliveryDays: number = 0;
  const isCanadian = address.country?.toLowerCase() == 'can' && ['on', 'ontario', 'qc', 'quebec'].includes(address.state?.toLowerCase());
  const isUSA = address.country?.toLowerCase() == 'usa';

  if (isCanadian || isUSA) deliveryDays = 7;
  else deliveryDays = 14;

  return format(subDays(refDate, deliveryDays), 'yyyy-MM-dd');
};

type TGetGiftRefEventDateProps = {
  addressParent?: string;
  groupedGifts?: Record<string, any>[];
  eventDate: string | Date;
};

export const getGiftRefEventDate = (props: TGetGiftRefEventDateProps) => {
  const { addressParent, groupedGifts, eventDate } = props;
  const refDate = typeof eventDate === 'string' ? parseISO(eventDate) : eventDate;

  if (addressParent != import.meta.env.REACT_APP_EMPLOYEES_TABLE_NAME) {
    return format(startOfMonth(refDate), 'yyyy-MM-dd');
  }

  if (groupedGifts && groupedGifts.length) {
    const youngestGift = (groupedGifts || []).sort((a, b) => {
      const dateA = a.eventDate ? new Date(a.eventDate).getTime() : new Date().getTime();
      const dateB = b.eventDate ? new Date(b.eventDate).getTime() : new Date().getTime();
      return dateA - dateB;
    })[0];
    const firstDate = youngestGift.eventDate ?? new Date();
    return format(typeof firstDate === 'string' ? parseISO(firstDate) : firstDate, 'yyyy-MM-dd');
  }

  return format(refDate, 'yyyy-MM-dd');
};

export const getIconFromGiftType = ({ type }: TGetGiftInfoSupportProps) => {
  switch (type) {
    case TGiftTypes.Birthday:
      return 'cake';
    case TGiftTypes.WorkAnniversary:
      return 'work';
    case TGiftTypes.NewHire:
      return 'person_add';
    default:
      return 'star';
  }
};

export const getReadableGiftType = ({ type }: TGetGiftInfoSupportProps) => {
  switch (type) {
    case TGiftTypes.Birthday:
      return 'Birthday';
    case TGiftTypes.WorkAnniversary:
      return 'Work Anniversary';
    case TGiftTypes.NewHire:
      return 'New Hire';
    case TGiftTypes.BabyGift:
      return 'Baby Gift';
    case TGiftTypes.Bereavement:
      return 'Bereavement';
    case TGiftTypes.Wedding:
      return 'Wedding/Engagement';
    case TGiftTypes.ReplacementWorkAnniversary:
      return 'Replacement Work Anniversary';
    case TGiftTypes.ReplacementEmployeeBirthday:
      return 'Replacement Employee Birthday';
    case TGiftTypes.CarePackage:
      return 'Care Package';
    case TGiftTypes.CustomBricks:
      return 'Custom Bricks';
    case TGiftTypes.Other:
      return 'Other';
  }
};

type TIsDonationGiftProps = {
  gift: TGiftType;
};

export const isDonationGift = (props: TIsDonationGiftProps) => {
  const { gift } = props;
  let { recipient } = gift;

  if (recipient == null) recipient = gift.recipient || undefined;

  if (recipient == null) return false;

  if (!isDependant(recipient) && ((gift.type === TGiftTypes.Birthday && recipient.donateBirthdayGift) || (gift.type === TGiftTypes.WorkAnniversary && recipient.donateWorkAnniversaryGift))) return true;

  if (isDependant(recipient) && recipient.type === TDependantType.SignificantOther && gift.type === TGiftTypes.Birthday && recipient.donateBirthdayGift) return true;

  return false;
};

type TPrintGiftLabelsProps = {
  gifts: TGiftType[];
};

export const printGiftLabels = async ({ gifts }: TPrintGiftLabelsProps) => {
  const docs: PDFDocument[] = [];
  // Build labels in selection order so office reads and PDF work stay sequential.
  for (const gift of gifts) {
    const { companyId, recipient, address, eventDate, company } = gift;
    const companyName = company?.name;
    const countryNameStr = gift.address?.country,
      countryName = countryNameStr ? ` | ${countryNameStr}` : '',
      workAnnVar = gift.type == TGiftTypes.WorkAnniversary ? ` | ${getReadableWorkAnniversaryVariation(gift.company?.workAnniversaryGiftVariation)}` : '';
    let officeName: string | null | undefined, recipientName: string | null | undefined, employeeName: string | null | undefined, recipientOfficeId: string | null | undefined;

    if (recipient) {
      recipientName = normalizeStringRelaxedASCII(recipient.fullName || '');
      if (isDependant(recipient) && recipient.employee) employeeName = normalizeStringRelaxedASCII(recipient.employee.fullName || '');

      if (!isDependant(recipient)) recipientOfficeId = recipient.officeId;
    }

    const [addressTableName, addressTableId] = (address?.id ?? '').split('|');
    if (addressTableName === import.meta.env.REACT_APP_OFFICES_TABLE_NAME) {
      const { data } = await client.query({
        query: GetOfficeDocument,
        fetchPolicy: 'network-only',
        variables: { id: recipientOfficeId ?? addressTableId, companyId }
      });
      officeName = normalizeStringRelaxedASCII(data.getOffice.name);
    }
    if (addressTableName === import.meta.env.REACT_APP_COMPANIES_TABLE_NAME) officeName = 'Company HQ';

    // PDF config
    const doc = await PDFDocument.create();
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const page = doc.addPage([54, 25]);

    let y = 25 - 1 - 3; // pageHeight - marginY - fontSize

    const write = (text?: string | null) => {
      if (!text) return;
      page.drawText(text, { x: 1, y, size: 3, font });
      y -= 4;
    };

    write(`${companyName}${countryName}`);
    write(officeName);
    write(recipientName);
    if (employeeName) write(`c/o: ${employeeName}`);
    write(readableDate(eventDate, 'yyyy-MM-dd'));
    write(`${getGiftEventType(gift)}${workAnnVar}`);
    docs.push(doc);
  }

  const pdfDoc = await PDFDocument.create();

  for (const doc of docs) {
    const copied = await pdfDoc.copyPages(doc, doc.getPageIndices());
    for (const page of copied) pdfDoc.addPage(page);
  }

  downloadFile({
    data: [await pdfDoc.save()] as BlobPart[],
    filetype: 'application/pdf',
    filename: 'print-labels.pdf'
  });
};

export const GIFT_TYPES = {
  [TGiftTypes.WorkAnniversary]: 'Work Anniversary',
  [TGiftTypes.Birthday]: 'Birthday',
  [TGiftTypes.NewHire]: 'New Hire'
};

export const ONE_OFF_GIFT_TYPES = {
  [TGiftTypes.BabyGift]: 'Baby Gift',
  [TGiftTypes.Bereavement]: 'Bereavement',
  [TGiftTypes.Wedding]: 'Wedding/Engagement',
  [TGiftTypes.ReplacementWorkAnniversary]: 'Replacement Work Anniversary',
  [TGiftTypes.ReplacementEmployeeBirthday]: 'Replacement Employee Birthday',
  [TGiftTypes.CarePackage]: 'Care Package',
  [TGiftTypes.CustomBricks]: 'Custom Bricks',
  [TGiftTypes.Other]: 'Other'
};

export const ALL_GIFT_TYPES = {
  ...GIFT_TYPES,
  ...ONE_OFF_GIFT_TYPES
};
